import { useState } from "react";

import BasePage from "../components/BasePage";
import Hero from "../components/Hero";

import imgR from "../assets/img/img-hero-r.webp";
import imgL from "../assets/img/img-hero-l.webp";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRightCircle, Clipboard, Check } from "lucide-react";

import NAUBtn from "../components/header/NAUBtn";

import TokenImageNAU from "../components/TokenImageNAU";
import TokenImageNAUY from "../components/TokenImageNAUY";
import TokenImageNAUN from "../components/TokenImageNAUN";
import NAUYBtn from "../components/header/NAUYBtn";
import NAUNBtn from "../components/header/NAUNBtn";

const CopyableAddress: React.FC = () => {
  const address = "0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913";
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(address);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error("Copy failed: ", err);
    }
  };

  return (
    <span className="inline-flex items-center bg-black/35 px-2 py-1  text-sm text-white space-x-1">
      <code className="select-all">{address}</code>
      <button
        onClick={handleCopy}
        className={`flex items-center space-x-1 px-2 py-1  text-xs focus:outline-none transition-colors ${
          copied ? "bg-green-600" : "bg-gray-700 hover:bg-gray-600"
        }`}
      >
        {copied ? (
          <>
            <Check size={16} />
          </>
        ) : (
          <>
            <Clipboard size={16} />
          </>
        )}
      </button>
    </span>
  );
};

const UcWavesPage: React.FC = () => {
  return (
    <BasePage
      title="UcWaves - Decentralized Opinion Sharing"
      description="UcWaves is an innovative decentralized opinion sharing platform on Base, enabling transparent, immutable, and community-driven opinion exchange."
      keywords="UcWaves, decentralized, blockchain, opinion sharing, Base, Ethereum"
    >
      {/* Hero Section */}
      <div className="relative">
        <Hero />
      </div>

      {/* Intro Section */}
      <section className="relative py-10 px-4 sm:px-6 lg:px-8 max-w-screen-2xl mx-auto text-justify">
        <img
          src={imgL}
          alt=""
          aria-hidden="true"
          className="hidden md:block absolute left-4 md:left-16 xl:left-32  top-5 transform -translate-y-5  w-24 md:w-40 xl:w-64 animate-fade-in-up object-contain z-10"
        />
        {/* Decorative image right */}
        <img
          src={imgR}
          alt=""
          aria-hidden="true"
          className="hidden md:block absolute right-4 md:right-16 xl:right-32 top-5 transform -translate-y-5 w-24 md:w-40 xl:w-64 animate-fade-in-up object-contain z-10"
        />

        {/* Content wrapper to ensure above images */}
        <div className="relative z-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.5,
              ease: "easeOut",
              type: "spring",
              stiffness: 200,
              damping: 20,
            }}
            viewport={{ once: true }}
            className="flex items-center justify-center"
          >
            <h1 className="text-6xl sm:text-6xl md:text-text-[64px]  md:mt-15  font-bold text-secondary-light text-center shadow-red-700 mb-32 ">
              UnCensored Waves
            </h1>
          </motion.div>
        </div>

        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{
            duration: 0.5,
            ease: "easeOut",
            type: "spring",
            stiffness: 200,
            damping: 20,
          }}
          viewport={{ once: true }}
          className="text-center text-primary-dark text-2xl sm:text-5xl font-bold mb-10 mt-10"
        >
          A Decentralized Opinion-Sharing Platform on Base
        </motion.h2>

        <div className="max-w-6xl mx-auto flex flex-col gap-6">
          <p className="text-gray-700 max-w-6xl mx-auto text-base sm:text-lg md:text-xl text-justify">
            UcWaves is an innovative project built on Base, a Layer 2 blockchain
            over Ethereum, designed to enable decentralized, tamper-proof, and
            community-driven opinion sharing on key topics. By leveraging
            blockchain technology, UcWaves ensures a secure, transparent, and
            gamified environment where token holders can participate in opinion
            sharing without the influence of media bias or poll manipulation.
          </p>
        </div>
      </section>

      {/* Problem / Solution Section */}
      <section className="pb-10 bg-[var(--color-background-light)] px-6 animate-fade-in-up delay-500 text-justify">
        <div className="max-w-6xl mx-auto grid gap-10">
          <div>
            <h3 className="text-xl font-bold text-[var(--color-primary)] mb-2">
              The Problem: Centralized Influence on Public Opinion
            </h3>
            <p className="text-gray-700 mb-2 text-justify">
              Traditional opinion-sharing mechanisms, such as polls and surveys,
              are often influenced by centralized entities, biased
              methodologies, and media manipulation. This creates a skewed
              representation of public sentiment, leading to misinformation and
              agenda-driven narratives.
            </p>
            <p className="text-gray-700 mb-4 text-justify">
              The lack of transparency and immutability in these systems allows
              for retroactive alterations, making it difficult to trust the
              authenticity of public opinions. This undermines the democratic
              process and stifles genuine community engagement.
            </p>
          </div>

          <div>
            <h3 className="text-xl font-bold text-[var(--color-primary)] mb-2">
              The Solution: Decentralized Opinion Sharing with UcWaves
            </h3>
            <p className="text-gray-700 mb-4 text-justify">
              UcWaves disrupts this paradigm by introducing a blockchain-powered
              opinion-sharing mechanism, where token holders express their
              opinions transparently and fairly. The ecosystem is structured as
              follows:
            </p>
            <ul className="list-disc pl-5 text-gray-700 space-y-1">
              <li>
                <strong>Token X:</strong> The core utility token of the
                ecosystem.
              </li>
              <li className="text-justify">
                <strong>Token Xyes / Token Xno:</strong> Users burn Token X to
                mint either Token Xyes or Token Xno—or a chosen ratio of their
                choice—on the UcWaves decentralized app, representing their
                stance on a given topic.
              </li>
              <li className="text-justify">
                <strong>Immutable and Transparent Opinion Sharing:</strong>{" "}
                Every opinion is recorded on-chain, ensuring tamper-proof,
                immutable, and unbiased results.
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="py-20 px-6 bg-[#7a1414] text-white text-center ">
        <h2 className="text-4xl sm:text-5xl font-bold mb-4 tracking-tight">
          Voice Your Opinion on North American Unity
        </h2>
        <div className="h-1 w-20 bg-white/40 mb-6 mx-auto rounded-full"></div>

        <div className="max-w-6xl mx-auto space-y-10 text-lg text-justify">
          {[
            {
              title: (
                <span className="inline-flex items-center gap-1">
                  <strong>Start with NAU</strong> <TokenImageNAU />
                </span>
              ),
              content: (
                <>
                  <strong>
                    NAU is your key to participating in North American Union
                    @UcWaves
                  </strong>
                  — the first ever opinion staking topic on the UcWaves
                  platform. <br />
                  Each NAU token represents your opportunity to take a stance on
                  whether North America should move toward greater unity — or
                  preserve national sovereignty.
                </>
              ),
            },
            {
              title: "Step 0: Join the NAU Presale",
              content: (
                <>
                  We're kicking off{" "}
                  <strong>North American Union @UcWaves</strong> with a
                  limited-time presale of <strong>NAU</strong>, our governance
                  token.
                  <br />
                  By joining the presale, you’ll get early access at a{" "}
                  <strong>15% discount</strong> compared to the launch price on
                  Uniswap.
                  <br />
                  <br />
                  👉{" "}
                  <a
                    // href="https://www.pinksale.finance/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 underline hover:opacity-80 transition"
                  >
                    Join the Presale (Link coming soon ...)
                  </a>
                  <br />
                  📘
                  <Link
                    to="/roadmap#roadmap-Presale"
                    className="text-blue-600 underline hover:opacity-80 transition"
                  >
                    View Full Presale Terms & Our Roadmap
                  </Link>
                </>
              ),
            },
            {
              title: "Step 1: Get NAU Tokens",
              content: (
                <>
                  Once the presale ends, you’ll be able to buy and trade{" "}
                  <strong>NAU</strong> directly on <strong>Uniswap</strong>{" "}
                  <NAUBtn /> .
                  <br />
                  <br />
                  To participate, you’ll need:
                  <br />• An EOA (Externally Owned Account) wallet like{" "}
                  <strong>MetaMask</strong>.
                  <br />• Some <strong>ETH on Base</strong> (around ~$5 worth)
                  to cover gas fees.
                  <br />• <strong>USDC (native)</strong> on Base — the
                  Circle-issued version at address:
                  <CopyableAddress />
                  <br />
                  <br />
                  ⚠️ <strong>Important:</strong> Make sure your{" "}
                  <strong>ETH</strong> and <strong>USDC</strong> are on the{" "}
                  <strong>Base network</strong>, not Ethereum Mainnet.
                  <br />
                  <br />
                  <strong>How to Get ETH and USDC on Base:</strong>
                  <br />• Bridge ETH from Ethereum using the{" "}
                  <a
                    href="https://docs.base.org/base-chain/network-information/bridges-mainnet#superbridge"
                    target="_blank"
                    className="text-blue-600 hover:text-blue-200 font-semibold"
                  >
                    Base Bridge
                  </a>
                  .
                  <br />• Or buy ETH and native USDC directly on Base via an
                  exchange like{" "}
                  <a
                    href="https://www.coinbase.com/"
                    target="_blank"
                    className="text-blue-600 hover:text-blue-200 font-semibold"
                  >
                    <strong>Coinbase</strong>
                  </a>
                  .
                  <br />
                  <br />
                  Once you're ready, head to <strong>
                    Uniswap
                  </strong> <NAUBtn /> and swap <strong>USDC</strong> for{" "}
                  <strong>NAU</strong>.
                </>
              ),
            },
            {
              title: "Step 2: Burn NAU on North American Union @UcWaves",
              content: (
                <>
                  Visit the{" "}
                  <strong>
                    UcWaves{" "}
                    <Link
                      to="/dApp"
                      className="text-blue-600 hover:text-blue-200 font-semibold"
                    >
                      MydApp
                    </Link>{" "}
                  </strong>{" "}
                  and connect your wallet (
                  <a
                    href="https://www.coinbase.com/wallet"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-200 font-semibold"
                  >
                    Coinbase Wallet
                  </a>
                  , or{" "}
                  <a
                    href="https://metamask.io/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-200 font-semibold"
                  >
                    MetaMask
                  </a>
                  ,{" "}
                  <a
                    href="https://uniswap.org/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-200 font-semibold "
                  >
                    Uniswap
                  </a>
                  ), For better user experience.
                  <br />
                  <br />
                  Select the amount of
                  <div className="flex items-center gap-1">
                    <strong>• NAU</strong> <TokenImageNAU />
                  </div>
                  tokens you want to burn and split them into opinion tokens:
                  <div className="flex items-center gap-1">
                    <strong>• NAUY</strong> <TokenImageNAUY />
                  </div>
                  ("now-ee") – for a <strong>YES</strong> stance
                  <div className="flex items-center gap-1">
                    <strong> • NAUN</strong> <TokenImageNAUN />
                  </div>
                  ("now-en") – for a <strong>NO</strong> stance
                  <br />
                  <br />
                  📌 <strong>Still deciding?</strong>
                  <br />
                  Stake your <strong>NAU</strong> tokens in the meantime and
                  earn more <strong>NAU</strong> while you reflect. Go to the{" "}
                  <Link
                    to="/dApp"
                    className="text-blue-600 hover:text-blue-200 font-semibold"
                  >
                    MydApp
                  </Link>{" "}
                  ,connect your wallet, and click on <strong>“Staking”</strong>{" "}
                  to load the staking platform. Enter the amount of NAU you want
                  to stake and click on <strong>“Stake NAU”</strong>. Your
                  staked tokens will be subject to a 7-day lockup, during which
                  you cannot unstake them — but you can{" "}
                  <strong>claim your earned rewards at any time</strong>. In the
                  dashboard, you can track your rewards in real time and view
                  the current <strong>reward rate</strong>, which reflects the
                  number of NAU distributed per second,{" "}
                  <strong>
                    prorated across all stakers based on their share of the
                    total staked NAU.
                  </strong>
                </>
              ),
            },
            {
              title: "Step 3: Choose Your Side(s)",
              content: (
                <>
                  Make your voice heard in the{" "}
                  <strong>North American Union</strong> debate.
                  <br />
                  <br />→ <strong>NAUY</strong> – if you support closer
                  integration:
                  <br />A united North America that leads in{" "}
                  <strong>economy</strong>, <strong>tech</strong>, and{" "}
                  <strong>culture</strong>.<br />
                  <br />→ <strong>NAUN</strong> – if you favor independence and
                  caution:
                  <br />A world where <strong>national identity</strong>,{" "}
                  <strong>local control</strong>, and{" "}
                  <strong>sovereignty</strong> take priority.
                  <br />
                  <br />
                  Once you’ve made your choice,{" "}
                  <strong>
                    mint your NAUY/NAUN opinion tokens. Go to the{" "}
                    <Link
                      to="/dApp"
                      className="text-blue-600 hover:text-blue-200 font-semibold"
                    >
                      MydApp
                    </Link>{" "}
                    , connect your wallet, choose the amount of NAU you want to
                    burn, adjust the ratio of NAUY and NAUN using the slider,
                    and click “Convert.”
                  </strong>{" "}
                  This is how{" "}
                  <strong>you decide the future of North America.</strong>
                </>
              ),
            },

            {
              title: "Step 4: Stake to Grow Your Voice",
              content: (
                <>
                  Whether you hold{" "}
                  <span className="inline-flex items-center gap-1">
                    <strong>NAUY</strong>
                  </span>
                  ,{" "}
                  <span className="inline-flex items-center gap-1">
                    <strong>NAUN</strong>
                  </span>
                  , or both — you can stake them on the <strong>UcWaves</strong>{" "}
                  platform to:
                  <br />• <strong>Earn rewards</strong>
                  <br />• <strong>Increase the visibility and weight</strong> of
                  your stance
                  <br />• <strong>Build your influence</strong> within the
                  debate
                  <br />
                  <p className="inline-flex flex-wrap items-center gap-x-2 mt-2">
                    • You can also trade
                    <span className="inline-flex items-center gap-1">
                      <strong>NAUY</strong> <NAUYBtn />
                    </span>
                    and
                    <span className="inline-flex items-center gap-1">
                      <strong>NAUN</strong> <NAUNBtn />
                    </span>
                    on Uniswap
                  </p>
                  <br />
                  🔍 Learn how in our{" "}
                  <a
                    href="#staking-section"
                    className="text-blue-600 hover:text-blue-200 font-semibold"
                  >
                    Staking Section
                  </a>
                  .
                </>
              ),
            },
            {
              title: "Your Opinion, Etched on the Blockchain",
              content: (
                <>
                  Your choice — <strong>YES</strong> or <strong>NO</strong> —
                  becomes part of a permanent,{" "}
                  <strong>censorship-resistant public ledger</strong>, visible
                  to all and <strong>immutable by design</strong>.<br />
                  <br />
                  This is not just a vote.
                  <br />
                  <strong>
                    This is an immutable record of where you stood when it
                    mattered.
                  </strong>
                </>
              ),
            },
            {
              title: "The Bigger Picture",
              content: (
                <>
                  <strong>North American Union @UcWaves</strong> is just the
                  beginning.
                  <br />
                  <br />
                  <strong>UcWaves</strong> is a platform for decentralized
                  opinion staking on major societal questions.
                  <br />
                  <br />
                  More topics — more tokens — more choices are coming.
                  <br />
                  <br />
                  <p className="text-blue-600 font-bold text-3xl text-center pt-5">
                    Make your voice count.
                  </p>
                  <p className="text-blue-600 font-bold text-3xl text-center pt-5">
                    Split your NAU. Claim your stance. Stake your future.
                  </p>
                </>
              ),
            },
          ].map(({ title, content }, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              viewport={{ once: true }}
              className="bg-white/10 backdrop-blur-sm border border-white/10 p-6 sm:p-8 shadow-lg"
            >
              <div className="flex items-start gap-4 mb-4">
                <ArrowRightCircle className="text-yellow-300 mt-1" size={20} />
                <h3 className="text-lg font-bold text-white">{title}</h3>
              </div>
              <div className="pl-8 text-gray-100 leading-relaxed space-y-2">
                {typeof content === "string" ? <p>{content}</p> : content}
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 bg-[var(--color-secondary-light)] text-white px-6 animate-fade-in-up delay-300">
        <div className="max-w-6xl mx-auto flex flex-col gap-8">
          {/* Title */}
          <div className="text-center">
            <h2 className="text-4xl font-bold text-[var(--color-primary)] tracking-tight">
              Beyond Just a Meme Coin
            </h2>
            <div className="h-1 w-20 bg-white/40 mt-4 mx-auto rounded-full"></div>
          </div>

          {/* Content */}
          <div className="relative backdrop-blur-md border border-white/10 bg-white/10 p-6 md:p-10 text-lg space-y-6 text-justify shadow-lg">
            <p className="leading-relaxed">
              While inspired by the meme coin culture, the UcWaves project goes
              beyond speculation by integrating a utility-driven mechanism that
              fosters genuine community participation. By aligning incentives,
              it encourages meaningful discussions and unfiltered public
              sentiment expression without external interference.
            </p>
            <p className="leading-relaxed">
              This decentralized approach to opinion sharing represents The New
              Paradigm in digital governance, allowing communities to voice
              their thoughts in a truly democratic and censorship-resistant
              manner.
            </p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="bg-[var(--color-background-light)] py-20">
        <div className="mx-auto max-w-6xl px-6 grid gap-10 md:grid-cols-3">
          {/** Feature 1 */}
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            whileInView={{ opacity: 1, y: 0, scale: 1 }}
            whileHover={{ scale: 1.03 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            viewport={{ once: true, amount: 0.2 }}
            className="relative group bg-white/10 backdrop-blur-lg border border-white/10 p-6 shadow-lg transition duration-300"
          >
            <h3 className="text-2xl font-semibold text-[var(--color-primary-dark)] mb-4">
              Decentralized
            </h3>
            <p className="text-[var(--color-text-secondary)] text-justify leading-relaxed">
              UcWaves operates on a fully decentralized model, ensuring that no
              single entity can control or manipulate public discourse. By
              leveraging blockchain technology on Base (Layer 2 over Ethereum),
              every opinion shared is governed by smart contracts and
              tokenomics, removing the influence of centralized institutions,
              media bias, or corporate agendas. This decentralization empowers
              individuals, allowing communities to engage in
              censorship-resistant discussions where every voice matters.
            </p>
          </motion.div>

          {/** Feature 2 */}
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            whileInView={{ opacity: 1, y: 0, scale: 1 }}
            whileHover={{ scale: 1.03 }}
            transition={{ duration: 0.5, ease: "easeOut", delay: 0.1 }}
            viewport={{ once: true, amount: 0.2 }}
            className="relative group bg-white/10 backdrop-blur-lg border border-white/10 p-6 shadow-lg transition duration-300"
          >
            <h3 className="text-2xl font-semibold text-[var(--color-primary-dark)] mb-4">
              Immutable
            </h3>
            <p className="text-[var(--color-text-secondary)] text-justify leading-relaxed">
              Every interaction on UcWaves is permanently recorded on-chain,
              making it tamper-proof and resistant to retroactive alterations.
              Unlike traditional polls and surveys that can be adjusted or
              removed, opinions expressed through Token Xyes and Token Xno
              remain immutable. This guarantees historical integrity, preventing
              misinformation, post-publication edits, or data manipulation,
              ensuring that the collective sentiment always remains verifiable.
            </p>
          </motion.div>

          {/** Feature 3 */}
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            whileInView={{ opacity: 1, y: 0, scale: 1 }}
            whileHover={{ scale: 1.03 }}
            transition={{ duration: 0.5, ease: "easeOut", delay: 0.2 }}
            viewport={{ once: true, amount: 0.2 }}
            className="relative group bg-white/10 backdrop-blur-lg border border-white/10 p-6 shadow-lg transition duration-300"
          >
            <h3 className="text-2xl font-semibold text-[var(--color-primary-dark)] mb-4">
              Transparent
            </h3>
            <p className="text-[var(--color-text-secondary)] text-justify leading-relaxed">
              Transparency is at the core of UcWaves. The smart contract-based
              opinion-sharing mechanism ensures that every transaction, token
              burn, and stance is visible on-chain. Unlike closed systems where
              poll results can be hidden or manipulated, UcWaves offers complete
              on-chain visibility, enabling users to independently verify
              results in real time. This fosters trust in public decision-making
              and ensures that all opinions are accountable, auditable, and free
              from hidden influence.
            </p>
          </motion.div>
        </div>
      </section>
    </BasePage>
  );
};

export default UcWavesPage;
